import * as bpmnDefinitions from "../constants/bpmnDefinitions";
import { storageKeys } from "../constants/storageKeys";

/**
 * Generates a customized JSON schema for BPMN process modeling based on user-selected properties.
 *
 * This function reads the selected BPMN elements and features from localStorage (using the SCHEMA_PROPERTIES key)
 * and dynamically constructs a JSON schema that describes the structure of a BPMN process model.
 * The schema includes pools, lanes, events, tasks, gateways, data objects, and other BPMN constructs,
 * with their availability and configuration determined by the user's selections.
 *
 * @returns {Object} The customized JSON schema object for BPMN process modeling.
 */
export const generateCustomizedSchema = () => {
    // Get selected properties from local storage
    const selectedProperties = JSON.parse(localStorage.getItem(storageKeys.SCHEMA_PROPERTIES)) || {};

    const EVENTS_RANGE = (selectedProperties[bpmnDefinitions.TIMER_EVENT] ? 1 : 0) + 
                         (selectedProperties[bpmnDefinitions.MESSAGE_RECEIVE_EVENT] ? 1 : 0) ;

    const schema = {
        type: "object",
        properties: {
            pools: {
                type: "array",
                ...(selectedProperties[bpmnDefinitions.MULTIPLE_POOLS] 
                    ? { description: "Multiple pools represent separate organizational units. Lanes are departments or participants within a pool." }
                    : { maxItems: 1, description: "Only one pool is allowed. Lanes are departments or participants." }),
                items: {
                    type: "object",
                    properties: {
                        name: { type: "string" },
                        ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? 
                            { dataObjects: {
                                type: "array",
                                items: {
                                    type: "object",
                                    description: "Use this only for digiatal or physical data files or persistent data stores (e.g., databases), not for general objects.",
                                    properties: {
                                        name: { type: "string" },
                                        type: { 
                                            type: "string", 
                                            enum: [
                                                ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] ? [bpmnDefinitions.DATA_OBJECT]: []), 
                                                ...(selectedProperties[bpmnDefinitions.DATA_STORE] ? [bpmnDefinitions.DATA_STORE]: [])],
                                        }
                                    },
                                    required: ["name", "type"]
                                }
                            }} 
                            : {}),
                        lanes: {
                            type: "array",
                            items: {
                                type: "object",
                                properties: {
                                    name: { type: "string" }
                                },
                                required: ["name"],
                                minItems: 1
                            }
                        },
                        startEvent:  {
                            oneOf: [
                                {
                                    type: "object",
                                    properties: {
                                        subType: { 
                                            type: "string", 
                                            enum: [bpmnDefinitions.DEFAULT_EVENT ], 
                                            description: "Default Start Event starts the process immediately."
                                        }, 
                                        name: { type: "string" },
                                        laneIndex: { type: "integer", description: "Index of the lane in the pool's lanes array" }
                                    },
                                    required: ["subType", "name", "laneIndex"]
                                },
                                ...(selectedProperties[bpmnDefinitions.MESSAGE_RECEIVE_EVENT] ? [{
                                    type: "object",
                                    properties: {
                                        subType: { type: "string", enum: [bpmnDefinitions.MESSAGE_RECEIVE_EVENT], 
                                            description: "Message Receive Start Event waits for an external message to arrive before starting the process."
                                        }, 
                                        id: { type: "integer"},
                                        name: { type: "string" },
                                        laneIndex: { type: "integer" },
                                        ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? 
                                            {writeDataObjectRefs: {
                                                type: "array",
                                                items: {
                                                    type: "integer"
                                                }
                                            }} 
                                            : {}),
                                    },
                                    required: ["subType", "id","name", "laneIndex"]
                                }] : []),
                                ...(selectedProperties[bpmnDefinitions.TIMER_EVENT] ? [{
                                    type: "object",
                                    properties: {
                                        subType: { type: "string", enum: [bpmnDefinitions.TIMER_EVENT], 
                                            description: "Start Event Timer waits for a specific time before starting the process."
                                        }, 
                                        name: { type: "string" },
                                        laneIndex: { type: "integer" }
                                    },
                                    required: ["subType", "name", "laneIndex"]
                                }] : [])
                            ].flat()
                        },
                        process: {
                            type: "array",
                            description: "The order in this array defines execution sequence.",
                            items: {
                                oneOf: [
                                    {
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.TASK] },
                                            subType: { 
                                                type: "string", 
                                                enum: [ bpmnDefinitions.ABSTRACT_TASK,  
                                                    ...(selectedProperties[bpmnDefinitions.USER_TASK] ? [bpmnDefinitions.USER_TASK]: []), 
                                                    ...(selectedProperties[bpmnDefinitions.MANUAL_TASK] ? [bpmnDefinitions.MANUAL_TASK]: []), 
                                                    ...(selectedProperties[bpmnDefinitions.SERVICE_TASK] ? [bpmnDefinitions.SERVICE_TASK]: []), 
                                                    ...(selectedProperties[bpmnDefinitions.SCRIPT_TASK] ? [bpmnDefinitions.SCRIPT_TASK]: []), 
                                                    ...(selectedProperties[bpmnDefinitions.BUSINESS_RULE_TASK] ? [bpmnDefinitions.BUSINESS_RULE_TASK]: []),
                                                    ...(selectedProperties[bpmnDefinitions.SUBPROCESS] ? [bpmnDefinitions.SUBPROCESS]: [])

                                                ] 
                                                    
                                                },
                                            name: { type: "string" },
                                            laneIndex: { type: "integer" },
                                            ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? 
                                                {readDataObjectRefs: {
                                                    type: "array",
                                                    description: "Indices of data objects to read from.",
                                                    items: {
                                                        type: "integer"
                                                    }
                                                },
                                                writeDataObjectRefs: {
                                                    type: "array",
                                                    description: "Indices of data objects to write to.",
                                                    items: {
                                                        type: "integer"
                                                    }
                                                }} 
                                                : {}),
                                        },
                                        required: ["type", "subType", "name", "laneIndex"]
                                    },
                                    ...(selectedProperties[bpmnDefinitions.TIMER_EVENT] 
                                        ? [{
                                            type: "object",
                                            properties: {
                                                type: { type: "string", enum: [bpmnDefinitions.EVENT] },
                                                subType: { 
                                                    type: "string", 
                                                    enum: [  
                                                        ...(selectedProperties[bpmnDefinitions.TIMER_EVENT] ? [bpmnDefinitions.TIMER_EVENT]: []), 
                                                    ] 
                                                    },
                                                name: { type: "string" },
                                                laneIndex: { type: "integer" }
                                            },
                                            required: ["type", "subType", "name", "laneIndex"]
                                    }] : []),  
                                    ...(selectedProperties[bpmnDefinitions.MESSAGE_RECEIVE_EVENT] ? [{
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.EVENT] },  
                                            subType: { type: "string", enum: [bpmnDefinitions.MESSAGE_RECEIVE_EVENT],                                           
                                            description: "Message Receive Event waits for an external message to arrive."
                                            }, 
                                            id: { type: "integer"},
                                            name: { type: "string" },
                                            laneIndex: { type: "integer" },
                                            ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? 
                                                {writeDataObjectRefs: {
                                                    type: "array",
                                                    items: {
                                                        type: "integer"
                                                    }
                                                }} 
                                                : {}),
                                        },
                                        required: ["type", "subType", "id", "name", "laneIndex"]
                                    }] : []),   
                                    ...(selectedProperties[bpmnDefinitions.MESSAGE_SEND_EVENT] ? [{
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.EVENT] },  
                                            subType: { type: "string", enum: [
                                                ...(selectedProperties[bpmnDefinitions.MESSAGE_SEND_EVENT] ? [bpmnDefinitions.MESSAGE_SEND_EVENT]: []), 
                                            ], 
                                            description: "Message Send Event sends an external message."
                                            }, 
                                            receiveEventId: { 
                                                type: "integer",
                                                description: "Id of corresponding Message Receive Event in another pool. Will be used to connect message flow."
                                            },
                                            name: { type: "string" },
                                            laneIndex: { type: "integer" },
                                            ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? 
                                                {readDataObjectRefs: {
                                                    type: "array",
                                                    items: {
                                                        type: "integer"                                                    }
                                                }} 
                                                : {}),
                                        },
                                        required: ["type", "subType", "name", "laneIndex"]
                                    }] : []),   
                                                               
                                    ...(selectedProperties[bpmnDefinitions.LOOP] ? [{
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.LOOP] },
                                            condition: { type: "string" },
                                            body: {
                                                type: "array",
                                                items: { "$ref": "#/properties/pools/items/properties/process/items" },
                                                minItems: 1
                                            },
                                            laneIndex: { type: "integer" }
                                        },
                                        required: ["type", "condition", "body", "laneIndex"]
                                    }] : []),
                                    ...(selectedProperties[bpmnDefinitions.PARALLEL_GATEWAY] ? [{
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.PARALLEL_GATEWAY] },
                                            branches: {
                                                type: "array",
                                                minItems: 2,
                                                items: {
                                                    type: "object",
                                                    properties: {
                                                        branch: {
                                                            type: "array",
                                                            items: { "$ref": "#/properties/pools/items/properties/process/items" }
                                                        }
                                                    },
                                                    required: ["branch"]
                                                }
                                            },
                                            laneIndex: { type: "integer" }
                                        },
                                        required: ["type", "branches", "laneIndex"]
                                    }] : []),
                                    ...(selectedProperties[bpmnDefinitions.EXCLUSIVE_GATEWAY] ? [{
                                        type: "object",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.EXCLUSIVE_GATEWAY] },
                                            condition: { type: "string", description: "Describe a decision point." },
                                            branches: {
                                                type: "array",
                                                minItems: 2,
                                                items: {
                                                    type: "object",
                                                    properties: {
                                                        label: { type: "string" },
                                                        branch: {
                                                            type: "array",
                                                            items: { "$ref": "#/properties/pools/items/properties/process/items" }
                                                        }
                                                    },
                                                    required: ["label", "branch"]
                                                }
                                            },
                                            laneIndex: { type: "integer", description: "Index of the lane of the participant taking the decision." }
                                        },
                                        required: ["type", "branches", "laneIndex"]
                                    }] : []),
                                    ...(selectedProperties[bpmnDefinitions.EVENT_BASED_GATEWAY] && EVENTS_RANGE ? [{
                                        type: "object",
                                        description : "Decision points based on external events. Waits for an event and directs process flow based on which event happens first",
                                        properties: {
                                            type: { type: "string", enum: [bpmnDefinitions.EVENT_BASED_GATEWAY] },
                                            branches: {
                                                type: "array",
                                                minItems: 2,
                                                items: {
                                                    type: "object",
                                                    properties: {
                                                        event: {                                                               
                                                            oneOf: (() => {
                                                                const references = [];
                                                                for (let i = 0; i < EVENTS_RANGE; i++) {
                                                                    references.push({ "$ref": `#/properties/pools/items/properties/process/items/oneOf/${i + 1}` });
                                                                }
                                                                return references;
                                                            })()
                                                        },
                                                        branch: {
                                                            type: "array",
                                                            items: { "$ref": "#/properties/pools/items/properties/process/items" }
                                                        }
                                                    },
                                                    required: ["event", "branch"]
                                                }
                                            },
                                            laneIndex: { type: "integer" }
                                        },
                                        required: ["type", "branches", "laneIndex"]
                                    }] : []),
                                ]
                            }
                        },
                        endEvent: {
                            type: "object",
                            properties: {
                                name: { type: "string" },
                                laneIndex: { type: "integer" }
                            },
                            required: ["name", "laneIndex"]
                        }
                    },
                    required: [
                        "name",
                        "lanes",
                        ...(selectedProperties[bpmnDefinitions.DATA_OBJECT] || selectedProperties[bpmnDefinitions.DATA_STORE] ? ["dataObjects"] : []),
                        "startEvent",
                        "process",
                        "endEvent"
                      ]
                }
            }
        },
        required: ["pools"],
        additionalProperties: false
    };

    return schema;
};